import moment from 'moment-timezone';

/**
 * This utility class is designed for date and time conversions based on timezones
 */
export class TimeZoneUtils {

    public static DATE_FORMAT = "MM/DD/YYYY";
    public static TIME_FORMAT = "H:mm A";
    public static DEFAULT_DATE = moment().format(TimeZoneUtils.DATE_FORMAT);
    public static SPACE_1 = " ";
    public static DATE_TIME_FORMAT = TimeZoneUtils.DATE_FORMAT + TimeZoneUtils.SPACE_1 + TimeZoneUtils.TIME_FORMAT;
    public static TIME_FORMAT_24 = "HH:mm";

    private static getReferenceDate() {
        return moment().format(TimeZoneUtils.DATE_FORMAT);
    }

    private static getSafeTimezone(userTimeZone: string) {
        const zone = (userTimeZone || '').trim();
        if (zone && moment.tz.zone(zone)) {
            return zone;
        }

        const guessedZone = moment.tz.guess();
        if (guessedZone && moment.tz.zone(guessedZone)) {
            return guessedZone;
        }

        return 'UTC';
    }

    private static normalizeInputTime(inputTime: string) {
        const normalizedTime = (inputTime || '').trim().toUpperCase();
        if (!normalizedTime) {
            return normalizedTime;
        }

        // Legacy slot value support: 24:00 PM => 0:00 AM (midnight)
        if (/^24:00(\s*[AP]M)?$/.test(normalizedTime)) {
            return '0:00 AM';
        }

        // Legacy slot value support: 13:00 PM => 13:00 (24h format)
        const meridiemMatch = normalizedTime.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/);
        if (meridiemMatch) {
            const hour = parseInt(meridiemMatch[1] || '0', 10);
            if (hour > 12) {
                return `${hour}:${meridiemMatch[2]}`;
            }
        }

        return normalizedTime;
    }

    private static parseTimeInUserTimezone(inputTime: string, userTimeZone: string) {
        const safeTimezone = TimeZoneUtils.getSafeTimezone(userTimeZone);
        const normalizedTime = TimeZoneUtils.normalizeInputTime(inputTime);
        const refDate = TimeZoneUtils.getReferenceDate();
        const dateTime = `${refDate}${TimeZoneUtils.SPACE_1}${normalizedTime}`;
        const formats = [
            TimeZoneUtils.DATE_TIME_FORMAT,
            `${TimeZoneUtils.DATE_FORMAT}${TimeZoneUtils.SPACE_1}${TimeZoneUtils.TIME_FORMAT_24}`,
            `${TimeZoneUtils.DATE_FORMAT}${TimeZoneUtils.SPACE_1}H:mm`,
            `${TimeZoneUtils.DATE_FORMAT}${TimeZoneUtils.SPACE_1}h:mm A`,
            `${TimeZoneUtils.DATE_FORMAT}${TimeZoneUtils.SPACE_1}hh:mm A`
        ];

        let parsed = moment.tz(dateTime, formats, true, safeTimezone);
        if (!parsed.isValid()) {
            parsed = moment.tz(dateTime, formats, safeTimezone);
        }
        return parsed;
    }

    private static parseUtcTime(inputTime: string) {
        const normalizedTime = TimeZoneUtils.normalizeInputTime(inputTime);
        const refDate = TimeZoneUtils.getReferenceDate();
        const dateTime = `${refDate}${TimeZoneUtils.SPACE_1}${normalizedTime}`;
        const formats = [
            TimeZoneUtils.DATE_TIME_FORMAT,
            `${TimeZoneUtils.DATE_FORMAT}${TimeZoneUtils.SPACE_1}${TimeZoneUtils.TIME_FORMAT_24}`,
            `${TimeZoneUtils.DATE_FORMAT}${TimeZoneUtils.SPACE_1}H:mm`,
            `${TimeZoneUtils.DATE_FORMAT}${TimeZoneUtils.SPACE_1}h:mm A`,
            `${TimeZoneUtils.DATE_FORMAT}${TimeZoneUtils.SPACE_1}hh:mm A`
        ];

        let parsed = moment.utc(dateTime, formats, true);
        if (!parsed.isValid()) {
            parsed = moment.utc(dateTime, formats);
        }
        return parsed;
    }

    /**
     * This method only converts Time to UTC Time
     * Ex: 09:00 AM, IST => 03:30 AM, UTC
     * 
     * @param time current time
     * @param userTimeZone timezone of current time
     * @returns UTC time
     */
    public static convertOnlyTimeToUTC(inputTime: string, userTimeZone: string) {
        return TimeZoneUtils.parseTimeInUserTimezone(inputTime, userTimeZone)
            .utc()
            .format(TimeZoneUtils.TIME_FORMAT);
    }

    /**
     * This method only converts UTC Time to User Time
     * Ex: 03:30 AM => 09:00 AM, IST
     * 
     * @param utcTime UTC time
     * @param userTimeZone user's timezone
     * @returns time in user's timezone
     */
    public static convertUTCToCurrentTime(utcTime: string, userTimeZone: string) {
        return TimeZoneUtils.parseUtcTime(utcTime)
            .tz(TimeZoneUtils.getSafeTimezone(userTimeZone))
            .format(TimeZoneUtils.TIME_FORMAT);
    }

    /**
     * This method converts UTC Time to user's local 24-hour time (HH:mm)
     * Ex: 03:30 AM UTC => 09:00 (IST)
     *
     * @param utcTime UTC time
     * @param userTimeZone user's timezone
     * @returns time in user's timezone in HH:mm format
     */
    public static convertUTCToCurrentTime24(utcTime: string, userTimeZone: string) {
        return TimeZoneUtils.parseUtcTime(utcTime)
            .tz(TimeZoneUtils.getSafeTimezone(userTimeZone))
            .format(TimeZoneUtils.TIME_FORMAT_24);
    }

    /**
     * This method converts Date Time to UTC Date Time
     * Ex: 21/05/2023 09:00 AM, IST => 21/05/2023 03:30 AM, UTC
     * 
     * @param inputDateTime current date time
     * @param userTimeZone timezone of current time
     * @returns UTC time
     */
    public static convertDateTimeToUTC(inputDateTime: string, userTimeZone: string) {
        return moment
            .tz(inputDateTime, TimeZoneUtils.DATE_TIME_FORMAT, userTimeZone)
            .utc()
            .format(TimeZoneUtils.DATE_TIME_FORMAT);
    }

    /**
     * This method only converts UTC Date Time to User Date Time
     * Ex: 21/05/2023 03:30 AM => 21/05/2023 09:00 AM, IST
     * 
     * @param inputDateTime UTC date time
     * @param userTimeZone user's timezone
     * @returns time in user's timezone
     */
    public static convertUTCToDateTime(inputDateTime: string, userTimeZone: string) {
        return moment
            .utc(inputDateTime, TimeZoneUtils.DATE_TIME_FORMAT)
            .tz(userTimeZone)
            .format(TimeZoneUtils.DATE_TIME_FORMAT);
    }
}